const UNKNOWN = "unknown/unknown";

/**
 * Determines Mime Type based on the first 8 bytes
 *
 * For more information see also:
 *
 * https://en.wikipedia.org/wiki/List_of_file_signatures
 * https://mimesniff.spec.whatwg.org/#matches-the-signature-for-mp4
 * https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
 * https://wiki.selfhtml.org/wiki/MIME-Type/%C3%9Cbersicht
 *
 * @param b Blob
 * @return Promise<Blob, string>
 */
async function determineMimeType(b: Blob): Promise<{ blob: Blob; mime: string }> {
  let header = "";
  try {
    // 16 bytes
    const buffer = await b.slice(0, 16).arrayBuffer();
    header = Array.from(new Uint8Array(buffer), (j) => j.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    console.warn("Could not read file header:", e);
    return { blob: b, mime: UNKNOWN };
  }

  // bytes [from, to) of the file
  const at = (from: number, to: number) => header.slice(from * 2, to * 2);

  const mime = (() => {
    switch (at(0, 4)) {
      case "89504e47":
        return "image/png";
      case "47494638":
        return "image/gif";
      case "ffd8ffe0":
      case "ffd8ffe1":
      case "ffd8ffe2":
      case "ffd8ffe3":
      case "ffd8ffe8":
      case "ffd8ffdb":
      case "ffd8ffee":
        return "image/jpeg";
      case "49492a00":
      case "4d4d002a":
        return "image/tiff";
      case "3c737667":
        return "image/svg+xml";
      case "25504446":
        return "application/pdf";
      case "d0cf11e0":
        // OLE2 compound file: legacy .doc/.xls/.ppt/.msg
        return "application/x-ole-storage";
      case "504b0304":
      case "504b0506":
      case "504b0708":
        return "application/zip";
      case "4f676753":
        return "audio/ogg";
      case "1a45dfa3":
        // EBML: webm or mkv
        return "video/webm";
      case "000001ba":
      case "000001b3":
        return "video/mpeg";
    }

    // UTF-32 BOMs must be tested before the 2-byte UTF-16 BOMs,
    // because 0000feff / fffe0000 share a prefix with them.
    if (at(0, 4) === "0000feff" || at(0, 4) === "fffe0000") return "text/plain";
    if (at(0, 3) === "efbbbf") return "text/plain";
    if (at(0, 2) === "fffe" || at(0, 2) === "feff") return "text/plain";

    if (at(0, 2) === "fffb" || at(0, 2) === "fff3" || at(0, 2) === "fff2") return "audio/mpeg";
    if (at(0, 3) === "494433") return "audio/mpeg"; // "ID3"

    // ISO-BMFF: "ftyp" at byte 4, brand at bytes 8..12.
    if (at(4, 8) === "66747970") {
      const brand = at(8, 12);
      if (brand.startsWith("3367")) return "video/3gp";
      return "video/mp4";
    }

    // RIFF container: form type at bytes 8..12.
    if (at(0, 4) === "52494646") {
      switch (at(8, 12)) {
        case "57454250": // "WEBP"
          return "image/webp";
        case "57415645": // "WAVE"
          return "audio/wav";
        case "41564920": // "AVI "
          return "video/avi";
        default:
          return UNKNOWN;
      }
    }

    // "<?xml " in UTF-8 / UTF-16LE / UTF-16BE / UTF-32LE / UTF-32BE
    if (
      at(0, 6) === "3c3f786d6c20" ||
      at(0, 11) === "3c003f0078006d006c0020" ||
      at(0, 12) === "003c003f0078006d006c0020" ||
      at(0, 16) === "3c0000003f000000780000006d0000006c00000020000000" ||
      at(0, 16) === "0000003c0000003f000000780000006d0000006c00000020"
    )
      return "text/xml";

    return UNKNOWN;
  })();

  return { blob: b, mime };
}

export { determineMimeType };
